import React, { useState, useEffect } from 'react';
import {
  TextField,
  Button,
  Grid,
  FormControlLabel,
  Checkbox,
  FormGroup,
  FormLabel,
  Box,
  Typography,
  Divider,
  CircularProgress,
  IconButton,
  Chip,
  List,
  ListItem,
  ListItemText,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import { Save as SaveIcon, Cancel as CancelIcon, Delete as DeleteIcon, Add as AddIcon, Search as SearchIcon, Science as ScienceIcon, Edit as EditIcon, LinkOff as LinkOffIcon } from '@mui/icons-material';
import { Company, CompanyCreate, CompanyUpdate, CompanyType } from '../../../types/company';
import { CORPORATE_STATUS_OPTIONS } from '../../../constants/corporateStatus';
import { COVERAGE_PRIORITY_OPTIONS } from '../../../constants/coveragePriority';
import { TechnologyListItem } from '../../../types/technology';
import { useFormValidation } from '../../../hooks/useFormValidation';
import { useAuth } from '../../../hooks/useAuth';
import { useEnvironment } from '../../../hooks/useEnvironment';
import ErrorMessage from '../../common/ErrorMessage/ErrorMessage';
import ConfirmationDialog from '../../safety/ConfirmationDialog/ConfirmationDialog';
import ReviewChangesDialog from '../../safety/ReviewChangesDialog/ReviewChangesDialog';
import { mapErrorToMessage } from '../../../utils/error-mapping';
import { addTechnologyToCompany, removeTechnologyFromCompany, getCompanyPipeline, addTherapeuticToCompany, updateDrugDevelopment, removeTherapeuticFromCompany, enrichCompany } from '../../../api/companies';
import { searchTechnologies } from '../../../api/technologies';
import { getAllDevelopmentStages, DevelopmentStage } from '../../../api/developmentStages';
import DiffReviewer from '../../common/DiffReviewer/DiffReviewer';
import { AutoFixHigh as AutoFixHighIcon } from '@mui/icons-material';

interface CompanyFormData extends Omit<CompanyCreate, 'former_names'> {
  former_names: string;
}

const parseFormerNamesInput = (raw: string): string[] => {
  const seen = new Set<string>();
  const names: string[] = [];
  for (const part of raw.split(',')) {
    const trimmed = part.trim();
    if (!trimmed) continue;
    const key = trimmed.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    names.push(trimmed);
  }
  return names;
};

interface CompanyFormProps {
  company?: Company | null;
  onSave: (data: CompanyCreate | CompanyUpdate, id?: string) => Promise<void>;
  onDelete?: (id: string) => Promise<void>;
  onCancel?: () => void;
  isCreate?: boolean; // Explicitly mark as a create operation (e.g., from bulk import)
}

const CompanyForm: React.FC<CompanyFormProps> = ({
  company,
  onSave,
  onDelete,
  onCancel,
  isCreate = false
}) => {
  const { user } = useAuth();
  const { environment } = useEnvironment();
  const isViewer = user?.role === 'viewer';
  const isProduction = environment === 'production';

  const [formData, setFormData] = useState<CompanyFormData>({
    id: '',
    name: '',
    website: '',
    product_website: '',
    ticker: '',
    exchange: '',
    corporate_status: '',
    former_names: '',
    acquired_by_company_id: '',
    summary_description: '',
    description: '',
    quartr_company_id: '',
    company_type: [],
    feed_sec: '',
    feed_news_rss: '',
    feed_events: '',
    feed_press_release: '',
    email_press_feed: '',
    press_release_url: '',
    website_content_archive_url: '',
    attribution_statement: '',
    update_technologies: false,
    completion_score: 0,
    coverage_priority: '',
  });

  const [originalData, setOriginalData] = useState<Company | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showReviewDialog, setShowReviewDialog] = useState(false);

  // Technology management state
  const [technologies, setTechnologies] = useState<Array<{ id: string; name: string }>>([]);
  const [technologySearchQuery, setTechnologySearchQuery] = useState('');
  const [technologySearchResults, setTechnologySearchResults] = useState<TechnologyListItem[]>([]);
  const [searchingTechnologies, setSearchingTechnologies] = useState(false);
  const [managingTechnology, setManagingTechnology] = useState(false);

  // Pipeline state
  const [pipeline, setPipeline] = useState<any[]>([]);
  const [loadingPipeline, setLoadingPipeline] = useState(false);

  // Therapeutic management state
  const [showAddTherapeutic, setShowAddTherapeutic] = useState(false);
  const [newTherapeutic, setNewTherapeutic] = useState({ name: '', stage: '', indication: '' });
  const [editingDrugDev, setEditingDrugDev] = useState<string | null>(null);
  const [editDrugDevData, setEditDrugDevData] = useState({ stage: '', indication: '' });
  const [managingPipeline, setManagingPipeline] = useState(false);

  // Development stages
  const [developmentStages, setDevelopmentStages] = useState<DevelopmentStage[]>([]);
  const [loadingStages, setLoadingStages] = useState(false);

  // AI Enrichment state
  const [enriching, setEnriching] = useState(false);
  const [diffReviewerOpen, setDiffReviewerOpen] = useState(false);
  const [enrichedData, setEnrichedData] = useState<any>(null);

  const { errors, validateAll, clearAllErrors } = useFormValidation({
    name: { required: true, minLength: 1 },
    id: {},
    website: {},
    product_website: {},
    ticker: {},
    exchange: {},
    corporate_status: {},
    former_names: {},
    acquired_by_company_id: {},
    summary_description: {},
    description: {},
    quartr_company_id: {},
    company_type: {},
    feed_sec: {},
    feed_news_rss: {},
    feed_events: {},
    feed_press_release: {},
    email_press_feed: {},
    press_release_url: {},
    website_content_archive_url: {},
    attribution_statement: {},
    update_technologies: {},
    completion_score: {},
    coverage_priority: {},
  });

  useEffect(() => {
    if (company) {
      const data = {
        id: company.id || '',
        name: company.name || '',
        website: company.website || '',
        product_website: company.product_website || '',
        ticker: company.ticker || '',
        exchange: company.exchange || '',
        corporate_status: company.corporate_status?.toLowerCase() || '',
        former_names: (company.former_names || []).join(', '),
        acquired_by_company_id: company.acquired_by_company_id || '',
        summary_description: company.summary_description || '',
        description: company.description || '',
        quartr_company_id: company.quartr_company_id || '',
        company_type: company.company_type || [],
        feed_sec: company.feed_sec || '',
        feed_news_rss: company.feed_news_rss || '',
        feed_events: company.feed_events || '',
        feed_press_release: company.feed_press_release || '',
        email_press_feed: company.email_press_feed || '',
        press_release_url: company.press_release_url || '',
        website_content_archive_url: company.website_content_archive_url || '',
        attribution_statement: company.attribution_statement || '',
        update_technologies: company.update_technologies || false,
        completion_score: company.completion_score || 0,
        coverage_priority: company.coverage_priority?.toLowerCase() || '',
      };
      setFormData(data);
      // Store original data for diff comparison
      setOriginalData(company);
      // Load technologies
      setTechnologies(company.technologies || []);
    } else {
      setFormData({
        id: '',
        name: '',
        website: '',
        product_website: '',
        ticker: '',
        exchange: '',
        corporate_status: '',
        former_names: '',
        acquired_by_company_id: '',
        summary_description: '',
        description: '',
        quartr_company_id: '',
        company_type: [],
        feed_sec: '',
        feed_news_rss: '',
        feed_events: '',
        feed_press_release: '',
        email_press_feed: '',
        press_release_url: '',
        website_content_archive_url: '',
        attribution_statement: '',
        update_technologies: false,
        completion_score: 0,
        coverage_priority: '',
      });
      setOriginalData(null);
      setTechnologies([]);
    }
    clearAllErrors();
  }, [company, clearAllErrors]);

  // Fetch development stages on mount
  useEffect(() => {
    const fetchStages = async () => {
      setLoadingStages(true);
      try {
        const stages = await getAllDevelopmentStages();
        setDevelopmentStages(stages);
      } catch (err: any) {
        console.error('Error fetching development stages:', err);
        setDevelopmentStages([]);
      } finally {
        setLoadingStages(false);
      }
    };

    fetchStages();
  }, []);

  // Fetch pipeline data when company changes
  useEffect(() => {
    const fetchPipeline = async () => {
      if (!company?.id) {
        setPipeline([]);
        return;
      }

      setLoadingPipeline(true);
      try {
        const pipelineData = await getCompanyPipeline(company.id);
        setPipeline(pipelineData);
      } catch (err: any) {
        console.error('Error fetching pipeline:', err);
        setPipeline([]);
      } finally {
        setLoadingPipeline(false);
      }
    };

    fetchPipeline();
  }, [company?.id]);

  const handleChange = (field: keyof CompanyCreate, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCompanyTypeChange = (type: CompanyType, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      company_type: checked
        ? [...(prev.company_type || []), type]
        : (prev.company_type || []).filter(t => t !== type)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateAll(formData)) {
      return;
    }

    // In production mode, show review dialog before saving
    if (isProduction) {
      setShowReviewDialog(true);
      return;
    }

    // In sandbox mode, save directly
    await performSave();
  };

  const performSave = async () => {
    setLoading(true);
    setError(null);
    clearAllErrors();
    setShowReviewDialog(false);

    try {
      // Create a copy of formData and remove id if it's empty
      const dataToSave: CompanyCreate = {
        ...formData,
        former_names: parseFormerNamesInput(formData.former_names),
      };
      if (!dataToSave.id || dataToSave.id.trim() === '') {
        delete dataToSave.id;
      }

      await onSave(dataToSave, company?.id);
    } catch (err: any) {
      setError(mapErrorToMessage(err, 'Failed to save company'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!company?.id || !onDelete) return;

    setLoading(true);
    setError(null);

    try {
      await onDelete(company.id);
      setShowDeleteConfirm(false);
    } catch (err: any) {
      setError(mapErrorToMessage(err, 'Failed to delete company'));
      setShowDeleteConfirm(false);
    } finally {
      setLoading(false);
    }
  };

  const handleTechnologySearch = async (query: string) => {
    setTechnologySearchQuery(query);

    if (!query.trim()) {
      setTechnologySearchResults([]);
      return;
    }

    setSearchingTechnologies(true);

    try {
      const results = await searchTechnologies(query);
      // Filter out technologies already added
      const filteredResults = results.filter(
        result => !technologies.some(tech => tech.id === result.id)
      );
      setTechnologySearchResults(filteredResults);
    } catch (err: any) {
      console.error('Error searching technologies:', err);
      setTechnologySearchResults([]);
    } finally {
      setSearchingTechnologies(false);
    }
  };

  const handleAddTechnology = async (technology: TechnologyListItem) => {
    if (!company?.id) return;

    setManagingTechnology(true);
    setError(null);

    try {
      await addTechnologyToCompany(company.id, technology.id);

      // Add to local state
      setTechnologies(prev => [...prev, { id: technology.id, name: technology.name }]);

      // Clear search
      setTechnologySearchQuery('');
      setTechnologySearchResults([]);
    } catch (err: any) {
      setError(mapErrorToMessage(err, 'Failed to add technology'));
    } finally {
      setManagingTechnology(false);
    }
  };

  const handleRemoveTechnology = async (technologyId: string) => {
    if (!company?.id) return;

    setManagingTechnology(true);
    setError(null);

    try {
      await removeTechnologyFromCompany(company.id, technologyId);

      // Remove from local state
      setTechnologies(prev => prev.filter(tech => tech.id !== technologyId));
    } catch (err: any) {
      setError(mapErrorToMessage(err, 'Failed to remove technology'));
    } finally {
      setManagingTechnology(false);
    }
  };

  // Therapeutic management handlers
  const handleAddTherapeutic = async () => {
    if (!company?.id || !newTherapeutic.name || !newTherapeutic.stage || !newTherapeutic.indication) {
      setError('Please fill in all fields (Name, Stage, Indication)');
      return;
    }

    setManagingPipeline(true);
    setError(null);

    try {
      await addTherapeuticToCompany(
        company.id,
        newTherapeutic.name,
        newTherapeutic.stage,
        newTherapeutic.indication
      );

      // Refresh pipeline
      const pipelineData = await getCompanyPipeline(company.id);
      setPipeline(pipelineData);

      // Reset form
      setNewTherapeutic({ name: '', stage: '', indication: '' });
      setShowAddTherapeutic(false);
    } catch (err: any) {
      setError(mapErrorToMessage(err, 'Failed to add therapeutic'));
    } finally {
      setManagingPipeline(false);
    }
  };

  const handleStartEditDrugDev = (drugDev: any) => {
    setEditingDrugDev(drugDev.id);
    setEditDrugDevData({
      stage: drugDev.development_stage?.name || '',
      indication: drugDev.indication?.name || '',
    });
  };

  const handleSaveEditDrugDev = async (drugDevId: string) => {
    if (!company?.id) return;

    setManagingPipeline(true);
    setError(null);

    try {
      await updateDrugDevelopment(
        company.id,
        drugDevId,
        editDrugDevData.stage || undefined,
        editDrugDevData.indication || undefined
      );

      // Refresh pipeline
      const pipelineData = await getCompanyPipeline(company.id);
      setPipeline(pipelineData);

      // Clear edit state
      setEditingDrugDev(null);
      setEditDrugDevData({ stage: '', indication: '' });
    } catch (err: any) {
      setError(mapErrorToMessage(err, 'Failed to update drug development'));
    } finally {
      setManagingPipeline(false);
    }
  };

  const handleRemoveTherapeutic = async (therapeuticId: string) => {
    if (!company?.id) return;

    setManagingPipeline(true);
    setError(null);

    try {
      await removeTherapeuticFromCompany(company.id, therapeuticId);

      // Refresh pipeline
      const pipelineData = await getCompanyPipeline(company.id);
      setPipeline(pipelineData);
    } catch (err: any) {
      setError(mapErrorToMessage(err, 'Failed to remove therapeutic'));
    } finally {
      setManagingPipeline(false);
    }
  };

  const handleEnrich = async () => {
    if (!company?.id) return;

    setEnriching(true);
    setError(null);

    try {
      const result = await enrichCompany(company.id);

      if (result.error) {
        setError(result.error);
        return;
      }

      if (result.proposed_changes && Object.keys(result.proposed_changes).length > 0) {
        // Extract just the 'new' value from the AI result format { old: ..., new: ... }
        const newValues: Record<string, any> = {};
        for (const [key, change] of Object.entries(result.proposed_changes)) {
          if (change && typeof change === 'object' && 'new' in change) {
            newValues[key] = (change as any).new;
          } else {
            newValues[key] = change;
          }
        }

        // Merge proposed changes with current form data so DiffReviewer shows only what changed
        // and doesn't treat missing fields in proposed_changes as deletions
        const fullEnrichedData = { ...formData, ...newValues };
        setEnrichedData(fullEnrichedData);
        setDiffReviewerOpen(true);
      } else {
        setError('No new information found by AI enrichment.');
      }
    } catch (err: any) {
      setError(mapErrorToMessage(err, 'Failed to enrich company with AI'));
    } finally {
      setEnriching(false);
    }
  };

  const handleApplyEnrichment = (finalData: any) => {
    // Only update fields that are in our form
    const updatedForm = { ...formData };

    // Map fields from finalData to updatedForm
    // finalData comes from the DiffReviewer after merging original and enrichment
    Object.keys(updatedForm).forEach(key => {
      if (finalData[key] !== undefined) {
        (updatedForm as any)[key] = finalData[key];
      }
    });

    setFormData(updatedForm);
    setDiffReviewerOpen(false);
  };

  return (
    <Box>
      {/* Header with Title and Delete Button */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h2">
          {company ? 'Edit Company' : 'New Company'}
        </Typography>
        {company && onDelete && !isViewer && (
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => setShowDeleteConfirm(true)}
            disabled={loading}
          >
            Delete Company
          </Button>
        )}
      </Box>

      {error && <ErrorMessage message={error} onDismiss={() => setError(null)} />}

      <form onSubmit={handleSubmit}>
        {/* Basic Information Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Basic Information
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Name"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                disabled={isViewer}
                error={!!errors.name}
                helperText={errors.name}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="ID"
                value={formData.id}
                onChange={(e) => handleChange('id', e.target.value)}
                disabled={isViewer || !!company}
                helperText={company ? "ID cannot be changed after creation" : "Leave blank to auto-generate from name"}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Website"
                value={formData.website}
                onChange={(e) => handleChange('website', e.target.value)}
                disabled={isViewer}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Product Website"
                value={formData.product_website}
                onChange={(e) => handleChange('product_website', e.target.value)}
                disabled={isViewer}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Ticker"
                value={formData.ticker}
                onChange={(e) => handleChange('ticker', e.target.value)}
                disabled={isViewer}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Exchange"
                value={formData.exchange}
                onChange={(e) => handleChange('exchange', e.target.value)}
                disabled={isViewer}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel id="corporate-status-label">Corporate Status</InputLabel>
                <Select
                  labelId="corporate-status-label"
                  value={formData.corporate_status}
                  label="Corporate Status"
                  onChange={(e) => handleChange('corporate_status', e.target.value)}
                  disabled={isViewer}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {CORPORATE_STATUS_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel id="coverage-priority-label">Coverage Priority</InputLabel>
                <Select
                  labelId="coverage-priority-label"
                  value={formData.coverage_priority || ''}
                  label="Coverage Priority"
                  onChange={(e) => handleChange('coverage_priority', e.target.value)}
                  disabled={isViewer}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {COVERAGE_PRIORITY_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Former names"
                value={formData.former_names}
                onChange={(e) => handleChange('former_names', e.target.value)}
                helperText="Comma-separated previous names after rebrands"
                disabled={isViewer}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Acquired by (company id)"
                value={formData.acquired_by_company_id}
                onChange={(e) => handleChange('acquired_by_company_id', e.target.value)}
                helperText="Set when corporate status is acquired"
                disabled={isViewer}
              />
            </Grid>

            <Grid item xs={12}>
              <FormLabel component="legend">Company Type</FormLabel>
              <FormGroup row>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.company_type?.includes(CompanyType.THERAPEUTIC)}
                      onChange={(e) => handleCompanyTypeChange(CompanyType.THERAPEUTIC, e.target.checked)}
                      disabled={isViewer}
                    />
                  }
                  label="Therapeutic"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.company_type?.includes(CompanyType.DEVICE)}
                      onChange={(e) => handleCompanyTypeChange(CompanyType.DEVICE, e.target.checked)}
                      disabled={isViewer}
                    />
                  }
                  label="Device"
                />
              </FormGroup>
            </Grid>
          </Grid>
        </Box>

        {/* Description Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Description
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={6}
                label="Summary Description"
                value={formData.summary_description}
                onChange={(e) => handleChange('summary_description', e.target.value)}
                disabled={isViewer}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={10}
                label="Detailed Description"
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                disabled={isViewer}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Technologies Section - Only show if company exists */}
        {company && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Technologies
            </Typography>
            <Divider sx={{ mb: 3 }} />

            {/* Current Technologies */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1.5, color: 'text.secondary' }}>
                Associated Technologies
              </Typography>
              {technologies.length === 0 ? (
                <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                  No technologies associated with this company yet.
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {technologies.map((tech) => (
                    <Chip
                      key={tech.id}
                      label={tech.name}
                      onDelete={!isViewer ? () => handleRemoveTechnology(tech.id) : undefined}
                      color="primary"
                      variant="outlined"
                      disabled={managingTechnology}
                    />
                  ))}
                </Box>
              )}
            </Box>

            {/* Search and Add Technologies */}
            {!isViewer && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1.5, color: 'text.secondary' }}>
                  Add Technology
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Search technologies to add..."
                  value={technologySearchQuery}
                  onChange={(e) => handleTechnologySearch(e.target.value)}
                  disabled={managingTechnology}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                    endAdornment: searchingTechnologies && (
                      <InputAdornment position="end">
                        <CircularProgress size={20} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 2 }}
                />

                {/* Search Results */}
                {technologySearchResults.length > 0 && (
                  <Box
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      maxHeight: '300px',
                      overflowY: 'auto',
                    }}
                  >
                    <List disablePadding>
                      {technologySearchResults.map((tech, index) => (
                        <ListItem
                          key={tech.id}
                          sx={{
                            borderBottom: index < technologySearchResults.length - 1 ? '1px solid' : 'none',
                            borderColor: 'divider',
                            cursor: 'pointer',
                            '&:hover': {
                              backgroundColor: 'action.hover',
                            },
                          }}
                          onClick={() => handleAddTechnology(tech)}
                          disabled={managingTechnology}
                        >
                          <ListItemText
                            primary={tech.name}
                            secondary={tech.classification}
                          />
                          <IconButton
                            edge="end"
                            color="primary"
                            disabled={managingTechnology}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAddTechnology(tech);
                            }}
                          >
                            <AddIcon />
                          </IconButton>
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}

                {technologySearchQuery && !searchingTechnologies && technologySearchResults.length === 0 && (
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic', mt: 1 }}>
                    No technologies found matching "{technologySearchQuery}"
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        )}

        {/* Product Pipeline Section - Only show if company exists */}
        {company && (
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Product Pipeline
              </Typography>
              {!isViewer && (
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => setShowAddTherapeutic(!showAddTherapeutic)}
                  disabled={managingPipeline}
                  size="small"
                >
                  {showAddTherapeutic ? 'Cancel' : 'Add Therapeutic'}
                </Button>
              )}
            </Box>
            <Divider sx={{ mb: 3 }} />

            {/* Add Therapeutic Form */}
            {showAddTherapeutic && !isViewer && (
              <Box
                sx={{
                  mb: 3,
                  p: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  backgroundColor: 'grey.50',
                }}
              >
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                  Add New Therapeutic
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Therapeutic Name"
                      value={newTherapeutic.name}
                      onChange={(e) => setNewTherapeutic({ ...newTherapeutic, name: e.target.value })}
                      disabled={managingPipeline}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="add-stage-label">Development Stage</InputLabel>
                      <Select
                        labelId="add-stage-label"
                        value={newTherapeutic.stage}
                        label="Development Stage"
                        onChange={(e) => setNewTherapeutic({ ...newTherapeutic, stage: e.target.value })}
                        disabled={managingPipeline || loadingStages}
                      >
                        {developmentStages.map((stage) => (
                          <MenuItem key={stage.id} value={stage.name}>
                            {stage.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Indication"
                      value={newTherapeutic.indication}
                      onChange={(e) => setNewTherapeutic({ ...newTherapeutic, indication: e.target.value })}
                      disabled={managingPipeline}
                      placeholder="e.g., Cancer"
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={handleAddTherapeutic}
                      disabled={managingPipeline || !newTherapeutic.name || !newTherapeutic.stage || !newTherapeutic.indication}
                      startIcon={managingPipeline ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
                    >
                      {managingPipeline ? 'Adding...' : 'Add'}
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            )}

            {loadingPipeline ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : pipeline.length === 0 ? (
              <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                No therapeutic products in the pipeline yet.
              </Typography>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Therapeutic</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Development Stage</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Indication</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Disease</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      {!isViewer && <TableCell sx={{ fontWeight: 600, width: 120 }}>Actions</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pipeline.map((therapeutic, tIndex) => {
                      const drugDevelopments = therapeutic.drug_developments || [];

                      if (drugDevelopments.length === 0) {
                        // Show therapeutic without developments
                        return (
                          <TableRow key={`${therapeutic.id || tIndex}`}>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <ScienceIcon fontSize="small" color="primary" />
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {therapeutic.name}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell colSpan={4}>
                              <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                                No development data available
                              </Typography>
                            </TableCell>
                            {!isViewer && (
                              <TableCell>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleRemoveTherapeutic(therapeutic.id)}
                                  disabled={managingPipeline}
                                  title="Remove from company"
                                >
                                  <LinkOffIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      }

                      // Show one row per drug development
                      return drugDevelopments.map((dev: any, index: number) => {
                        const isEditing = editingDrugDev === dev.id;

                        return (
                          <TableRow key={`${therapeutic.id || tIndex}-${dev.id || 'nodev'}-${tIndex}-${index}`}>
                            {index === 0 && (
                              <TableCell rowSpan={drugDevelopments.length}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <ScienceIcon fontSize="small" color="primary" />
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {therapeutic.name}
                                  </Typography>
                                </Box>
                              </TableCell>
                            )}
                            <TableCell>
                              {isEditing ? (
                                <FormControl fullWidth size="small">
                                  <Select
                                    value={editDrugDevData.stage}
                                    onChange={(e) => setEditDrugDevData({ ...editDrugDevData, stage: e.target.value })}
                                    disabled={managingPipeline || loadingStages}
                                    displayEmpty
                                  >
                                    <MenuItem value="">
                                      <em>Select Stage</em>
                                    </MenuItem>
                                    {developmentStages.map((stage) => (
                                      <MenuItem key={stage.id} value={stage.name}>
                                        {stage.name}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              ) : dev.development_stage ? (
                                <Chip
                                  label={dev.development_stage.name}
                                  size="small"
                                  color="info"
                                  variant="outlined"
                                />
                              ) : (
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                  -
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              {isEditing ? (
                                <TextField
                                  size="small"
                                  fullWidth
                                  value={editDrugDevData.indication}
                                  onChange={(e) => setEditDrugDevData({ ...editDrugDevData, indication: e.target.value })}
                                  placeholder="Indication"
                                  disabled={managingPipeline}
                                />
                              ) : (
                                <Typography variant="body2">
                                  {dev.indication?.name || '-'}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {dev.indication?.disease?.name || '-'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {dev.status ? (
                                <Chip
                                  label={dev.status}
                                  size="small"
                                  color={dev.status.toLowerCase() === 'active' ? 'success' : 'default'}
                                  variant="outlined"
                                />
                              ) : (
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                  -
                                </Typography>
                              )}
                            </TableCell>
                            {!isViewer && (
                              <TableCell>
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                  {isEditing ? (
                                    <>
                                      <IconButton
                                        size="small"
                                        color="primary"
                                        onClick={() => handleSaveEditDrugDev(dev.id)}
                                        disabled={managingPipeline}
                                        title="Save changes"
                                      >
                                        <SaveIcon fontSize="small" />
                                      </IconButton>
                                      <IconButton
                                        size="small"
                                        onClick={() => {
                                          setEditingDrugDev(null);
                                          setEditDrugDevData({ stage: '', indication: '' });
                                        }}
                                        disabled={managingPipeline}
                                        title="Cancel"
                                      >
                                        <CancelIcon fontSize="small" />
                                      </IconButton>
                                    </>
                                  ) : (
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      onClick={() => handleStartEditDrugDev(dev)}
                                      disabled={managingPipeline}
                                      title="Edit development"
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  )}
                                  {index === 0 && (
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => handleRemoveTherapeutic(therapeutic.id)}
                                      disabled={managingPipeline}
                                      title="Remove from company"
                                    >
                                      <LinkOffIcon fontSize="small" />
                                    </IconButton>
                                  )}
                                </Box>
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      });
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}

        {/* External IDs & Feeds Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            External IDs & Feeds
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Quartr Company ID"
                value={formData.quartr_company_id}
                onChange={(e) => handleChange('quartr_company_id', e.target.value)}
                disabled={isViewer}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="SEC Feed URL"
                value={formData.feed_sec}
                onChange={(e) => handleChange('feed_sec', e.target.value)}
                disabled={isViewer}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="News RSS Feed URL"
                value={formData.feed_news_rss}
                onChange={(e) => handleChange('feed_news_rss', e.target.value)}
                disabled={isViewer}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Events Feed URL"
                value={formData.feed_events}
                onChange={(e) => handleChange('feed_events', e.target.value)}
                disabled={isViewer}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Press Release Feed URL"
                value={formData.feed_press_release}
                onChange={(e) => handleChange('feed_press_release', e.target.value)}
                disabled={isViewer}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Press Release URL"
                value={formData.press_release_url}
                onChange={(e) => handleChange('press_release_url', e.target.value)}
                disabled={isViewer}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email Press Feed"
                value={formData.email_press_feed}
                onChange={(e) => handleChange('email_press_feed', e.target.value)}
                disabled={isViewer}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Additional Information Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Additional Information
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Website Content Archive URL"
                value={formData.website_content_archive_url}
                onChange={(e) => handleChange('website_content_archive_url', e.target.value)}
                disabled={isViewer}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Attribution Statement"
                value={formData.attribution_statement}
                onChange={(e) => handleChange('attribution_statement', e.target.value)}
                disabled={isViewer}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Completion Score (0-100)"
                value={formData.completion_score}
                onChange={(e) => handleChange('completion_score', parseInt(e.target.value) || 0)}
                disabled={isViewer}
                inputProps={{ min: 0, max: 100 }}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.update_technologies || false}
                    onChange={(e) => handleChange('update_technologies', e.target.checked)}
                    disabled={isViewer}
                  />
                }
                label="Update Technologies (Flag for technology data refreshes)"
              />
              <Typography variant="caption" sx={{ display: 'block', ml: 4, mt: -1, color: 'text.secondary' }}>
                Enable this flag to mark this company for technology data updates or refreshes
              </Typography>
            </Grid>
          </Grid>
        </Box>

        {/* Form Actions */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 4 }}>
          {onCancel && (
            <Button
              variant="outlined"
              startIcon={<CancelIcon />}
              onClick={onCancel}
              size="large"
            >
              Cancel
            </Button>
          )}
          {company && !isViewer && (user?.role === 'editor' || user?.role === 'admin') && (
            <Button
              variant="outlined"
              color="secondary"
              startIcon={enriching ? <CircularProgress size={20} color="inherit" /> : <AutoFixHighIcon />}
              onClick={handleEnrich}
              disabled={loading || enriching}
              size="large"
            >
              {enriching ? 'Enriching...' : 'Enrich with AI'}
            </Button>
          )}
          {!isViewer && (
            <Button
              type="submit"
              variant="contained"
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              disabled={loading}
              size="large"
            >
              {loading ? 'Saving...' : 'Save'}
            </Button>
          )}
        </Box>
      </form>

      <ConfirmationDialog
        isOpen={showDeleteConfirm}
        title="Delete Company"
        message={`Are you sure you want to delete "${company?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteConfirm(false)}
        isDangerous={true}
        requireDoubleConfirm={isProduction}
        doubleConfirmMessage={`You are about to permanently delete "${company?.name}" from the PRODUCTION database. This action is irreversible and cannot be undone.`}
      />

      <ReviewChangesDialog
        isOpen={showReviewDialog}
        title={isCreate ? "Review Production Create" : "Review Production Changes"}
        originalData={
          // If explicitly marked as create, use null for originalData
          // Otherwise, use cleaned originalData for UPDATE comparison
          isCreate
            ? null
            : originalData ? {
              id: originalData.id,
              name: originalData.name,
              website: originalData.website,
              product_website: originalData.product_website,
              ticker: originalData.ticker,
              exchange: originalData.exchange,
              corporate_status: originalData.corporate_status,
              former_names: (originalData.former_names || []).join(', '),
              acquired_by_company_id: originalData.acquired_by_company_id,
              summary_description: originalData.summary_description,
              description: originalData.description,
              quartr_company_id: originalData.quartr_company_id,
              company_type: originalData.company_type,
              feed_sec: originalData.feed_sec,
              feed_news_rss: originalData.feed_news_rss,
              feed_events: originalData.feed_events,
              feed_press_release: originalData.feed_press_release,
              email_press_feed: originalData.email_press_feed,
              press_release_url: originalData.press_release_url,
              website_content_archive_url: originalData.website_content_archive_url,
              attribution_statement: originalData.attribution_statement,
              update_technologies: originalData.update_technologies,
              completion_score: originalData.completion_score,
              coverage_priority: originalData.coverage_priority,
            } : null
        }
        modifiedData={{
          ...formData,
          ...(technologies.length > 0 ? { technologies } : {})
        }}
        onConfirm={performSave}
        onCancel={() => setShowReviewDialog(false)}
        entityType="company"
      />

      <DiffReviewer
        isOpen={diffReviewerOpen}
        originalData={formData}
        enrichedData={enrichedData}
        onApply={handleApplyEnrichment}
        onCancel={() => setDiffReviewerOpen(false)}
        title={`Review AI Enrichment for ${formData.name}`}
      />
    </Box>
  );
};

export default CompanyForm;
